import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { apiGet, apiPost, apiPut } from '@/api/client';
import type {
  SupportTicketDetail,
  SupportTicketPriority,
  SupportTicketStatus,
  User,
} from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Badge, type BadgeTone } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Field, Select, Textarea } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

const STATUSES: readonly SupportTicketStatus[] = ['open', 'in_progress', 'resolved', 'closed'];
const PRIORITIES: readonly SupportTicketPriority[] = ['low', 'normal', 'high', 'urgent'];

function statusTone(status: SupportTicketStatus | undefined): BadgeTone {
  switch (status) {
    case 'open':
      return 'warning';
    case 'in_progress':
      return 'mint';
    default:
      return 'outline';
  }
}

/**
 * "Beheer › Support" ticket detail (PLAN-FASE3.md WP-01): the thread, a
 * reply field, and status/priority controls — the whole platform-staff
 * workflow for one ticket. Every mutation reloads the ticket afterwards
 * (no optimistic update) so the thread and the status badge always reflect
 * what the API actually persisted.
 */
export function SupportTicketDetailPage() {
  const { t } = useTranslation();
  const { ticketId = '' } = useParams();

  const ticket = useAsync(
    () =>
      apiGet('/support/tickets/{ticketId}', {
        params: { ticketId },
      }) as Promise<SupportTicketDetail>,
    [ticketId],
  );

  return (
    <section className="page flex flex-col gap-6">
      <AsyncSection state={ticket} label={t('manageSupport.detail.thisTicket')}>
        {(data) => (
          <>
            <PageHeader
              title={data.subject ?? ''}
              meta={
                <>
                  <Badge tone={statusTone(data.status)}>
                    {data.status ? t(`manageSupport.status.${data.status}`) : '—'}
                  </Badge>{' '}
                  <Badge tone="outline">
                    {data.priority ? t(`manageSupport.priority.${data.priority}`) : '—'}
                  </Badge>
                </>
              }
            />

            <RequesterCard userId={data.user_id} />

            <StatusPriorityCard
              ticketId={ticketId}
              status={data.status ?? 'open'}
              priority={data.priority ?? 'normal'}
              onDone={ticket.reload}
            />

            <ThreadCard messages={data.messages ?? []} ticketId={ticketId} onDone={ticket.reload} />
          </>
        )}
      </AsyncSection>
    </section>
  );
}

function RequesterCard({ userId }: { userId: string | undefined }) {
  const { t } = useTranslation();
  const requester = useAsync(
    () =>
      userId
        ? (apiGet('/users/{userId}', { params: { userId } }) as Promise<User>)
        : Promise.resolve(undefined),
    [userId],
  );

  return (
    <Card aria-label={t('manageSupport.detail.requesterTitle')}>
      <CardHeader title={t('manageSupport.detail.requesterTitle')} />
      <AsyncSection state={requester} label={t('manageSupport.detail.requesterLabel')}>
        {(data) => (
          <p className="text-ink text-sm">
            {data?.name ?? data?.nickname ?? userId ?? '—'}
            {data?.email ? ` · ${data.email}` : ''}
          </p>
        )}
      </AsyncSection>
    </Card>
  );
}

function StatusPriorityCard({
  ticketId,
  status,
  priority,
  onDone,
}: {
  ticketId: string;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [statusValue, setStatusValue] = useState(status);
  const [priorityValue, setPriorityValue] = useState(priority);

  const form = useSubmit(async () => {
    await apiPut('/support/tickets/{ticketId}', {
      params: { ticketId },
      body: { status: statusValue, priority: priorityValue },
    });
    onDone();
  });

  return (
    <Card aria-label={t('manageSupport.detail.controlsTitle')}>
      <CardHeader title={t('manageSupport.detail.controlsTitle')} />
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void form.submit();
        }}
      >
        <Field label={t('manageSupport.page.status')} className="w-56">
          <Select
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value as SupportTicketStatus)}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`manageSupport.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('manageSupport.page.priority')} className="w-56">
          <Select
            value={priorityValue}
            onChange={(e) => setPriorityValue(e.target.value as SupportTicketPriority)}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {t(`manageSupport.priority.${p}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" disabled={form.pending}>
          {t('manageSupport.detail.save')}
        </Button>
        <SubmitError error={form.error} />
      </form>
    </Card>
  );
}

function ThreadCard({
  messages,
  ticketId,
  onDone,
}: {
  messages: NonNullable<SupportTicketDetail['messages']>;
  ticketId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState('');

  const form = useSubmit(async () => {
    await apiPost('/support/tickets/{ticketId}/messages', {
      params: { ticketId },
      body: { body },
    });
    setBody('');
    onDone();
  });

  return (
    <Card aria-label={t('manageSupport.detail.threadTitle')}>
      <CardHeader title={t('manageSupport.detail.threadTitle')} />
      {messages.length === 0 ? (
        <p className="text-accent text-sm">{t('manageSupport.detail.threadEmpty')}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {messages.map((message) => (
            <li key={message.id} className="border-ink/10 rounded-xl border p-3">
              <p className="text-accent text-[13px]">
                {message.author_user_id} · {message.created_at}
              </p>
              <p className="text-ink text-sm whitespace-pre-wrap">{message.body}</p>
            </li>
          ))}
        </ul>
      )}

      <form
        className="mt-4 flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void form.submit();
        }}
      >
        <Field label={t('manageSupport.detail.replyLabel')}>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} required />
        </Field>
        <div className="flex items-center justify-end gap-3">
          <SubmitError error={form.error} />
          <Button type="submit" disabled={form.pending || body.trim() === ''}>
            {t('manageSupport.detail.reply')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
